import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from './client'
import type { DateRange } from '@/lib/dateRange'

// ============================================================
//  Auto-Harvest API-Hooks
//  Spec: frontend/.handoff/04_BACKEND_TASKS.md §1
// ============================================================

export type HarvestRecommendation = 'promote' | 'negate' | 'ignore'
export type HarvestStatus = 'candidate' | 'promoted' | 'negated' | 'ignored'
export type HarvestMatchType = 'broad' | 'phrase' | 'exact'
export type HarvestNegativeMatchType = 'negative_exact' | 'negative_phrase'
export type HarvestScope = 'ad-group' | 'campaign'
// Spine-3-Integration (Fenster I, GLB-1): 'applying' = laufender Apply-Claim
// (transient, keine Entscheidung möglich), 'reconcile_required' = Apply-Versuch mit
// unbekanntem Amazon-Ausgang (über approve/reject auflösbar, s. HarvestWorkspace).
export type PipelineHarvestStatus = 'pending' | 'approved' | 'rejected' | 'applied' | 'applying' | 'reconcile_required'

export interface HarvestFunnel {
  discovery: number
  candidates: number
  promoted: number
  negated: number
}

export interface HarvestSearchTerm {
  id: number
  term: string
  sourceCampaignId: number
  sourceCampaignName: string
  sourceMatchType: HarvestMatchType | null
  spend: number
  sales: number
  orders: number
  clicks: number
  impressions: number
  acos: number | null
  recommendation: HarvestRecommendation
  suggestedTargetCampaignId: number | null
  suggestedTargetCampaignName: string | null
  suggestedMatchType: HarvestMatchType
  suggestedBid: number | null
  currentBid: number | null
  status: HarvestStatus
}

export interface HarvestPipelineEntry {
  id: number
  term: string
  targetCampaign: { id: number; name: string }
  matchType: HarvestMatchType
  bid: number | null
  status: PipelineHarvestStatus
  promotedAt: string
}

export interface HarvestNegative {
  id: number
  keywordText: string
  matchType: HarvestNegativeMatchType
  scope: HarvestScope
  campaignId: number | null
  campaignName: string | null
  state: string
  createdAt: string
}

function applyRange(params: URLSearchParams, range?: DateRange) {
  if (range) {
    params.set('dateFrom', range.from)
    params.set('dateTo', range.to)
  }
}

export function useHarvestFunnel(range?: DateRange) {
  const params = new URLSearchParams()
  applyRange(params, range)
  const qs = params.toString()
  return useQuery({
    queryKey: ['harvest', 'funnel', range?.from ?? null, range?.to ?? null],
    queryFn: () => api.get<{ funnel: HarvestFunnel; windowDays: number; dateFrom: string; dateTo: string }>(
      `/harvest/funnel/${qs ? `?${qs}` : ''}`,
    ),
  })
}

export function useHarvestSearchTerms(filters: {
  recommendation?: HarvestRecommendation
  status?: HarvestStatus
  range?: DateRange
  limit?: number
  offset?: number
} = {}) {
  const params = new URLSearchParams()
  if (filters.recommendation) params.set('recommendation', filters.recommendation)
  if (filters.status) params.set('status', filters.status)
  applyRange(params, filters.range)
  if (filters.limit) params.set('limit', String(filters.limit))
  if (filters.offset) params.set('offset', String(filters.offset))
  const qs = params.toString()
  return useQuery({
    queryKey: ['harvest', 'search-terms', filters.recommendation ?? null, filters.status ?? null, filters.range?.from ?? null, filters.range?.to ?? null, filters.limit ?? null, filters.offset ?? null],
    queryFn: () => api.get<{ searchTerms: HarvestSearchTerm[]; total: number }>(
      `/harvest/search-terms/${qs ? `?${qs}` : ''}`,
    ),
  })
}

export function useHarvestPipeline(range?: DateRange) {
  const params = new URLSearchParams()
  applyRange(params, range)
  const qs = params.toString()
  return useQuery({
    queryKey: ['harvest', 'pipeline', range?.from ?? null, range?.to ?? null],
    queryFn: () => api.get<{ pipeline: HarvestPipelineEntry[]; total: number }>(
      `/harvest/pipeline/${qs ? `?${qs}` : ''}`,
    ),
  })
}

export function useHarvestNegatives(filters: { scope?: HarvestScope; search?: string; range?: DateRange } = {}) {
  const params = new URLSearchParams()
  if (filters.scope) params.set('scope', filters.scope)
  if (filters.search) params.set('search', filters.search)
  applyRange(params, filters.range)
  const qs = params.toString()
  return useQuery({
    queryKey: ['harvest', 'negatives', filters.scope ?? null, filters.search ?? null, filters.range?.from ?? null, filters.range?.to ?? null],
    queryFn: () => api.get<{ negatives: HarvestNegative[]; total: number }>(
      `/harvest/negatives/${qs ? `?${qs}` : ''}`,
    ),
  })
}

export function useHarvestPromote() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: {
      searchTermIds: number[]
      targetCampaignId: number
      targetAdGroupId: number
      matchType: HarvestMatchType
      bid: number
    }) => api.post<{ createdKeywordIds: number[]; skipped: unknown[] }>('/harvest/promote/', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['harvest'] })
    },
  })
}

export function useHarvestNegate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: {
      searchTermIds: number[]
      scope: HarvestScope
      matchType: HarvestNegativeMatchType
    }) => api.post<{ createdNegativeIds: number[]; skipped: unknown[] }>('/harvest/negate/', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['harvest'] })
    },
  })
}

// v29 Phase A — Pipeline-Aktionen (Approve/Reject im Demo-Modus erlaubt, Apply blockiert mit 423)

export function useApprovePipelineEntry() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      api.post<{ id: number; harvestStatus: PipelineHarvestStatus }>(
        `/harvest/pipeline/${id}/approve/`,
        {},
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['harvest', 'pipeline'] })
    },
  })
}

export function useRejectPipelineEntry() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      api.post<{ id: number; harvestStatus: PipelineHarvestStatus }>(
        `/harvest/pipeline/${id}/reject/`,
        {},
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['harvest', 'pipeline'] })
    },
  })
}

export function useApplyPipelineEntry() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      api.post<{ id: number; harvestStatus: PipelineHarvestStatus }>(
        `/harvest/pipeline/${id}/apply/`,
        {},
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['harvest', 'pipeline'] })
    },
  })
}
